/**
 * DLE 評価の**集計表示**側の型と配色 (MyPage「DLE評価」タブ)。
 *
 * 回答入力側 (`dle-evaluation.ts`) は設問文と選択肢説明の SSOT で、こちらは
 * 「4 軸をどの色・どのスケールで描くか」の SSOT。radar / 推移折線が共有する。
 *
 * 軸名は集計シートの `全体分析` タブの見出し (`一次回答平均` 等) に合わせてある。
 * 画面の軸名と運用者が見ているシートの列名が一致していないと、点が合わないときに
 * どちらを直すのかが分からなくなる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE 評価 radar / 推移の軸定義・配色・点数スケールと集計レスポンスの型
 */

import type { DleAxisKey } from './dle-evaluation.js';

/** 軸別の値 (平均点)。1 件も評点が無い軸は null。 @graph-connects none */
export type DleAxisScores = Record<DleAxisKey, number | null>;
/** 軸別の有効回答数。 @graph-connects none */
export type DleAxisCounts = Record<DleAxisKey, number>;
/**
 * 軸別のコメント本文一覧。
 * **本人の系列にしか入らない**（全体平均の系列は常に空）。回答者名は含まれない。
 * @graph-connects none
 */
export type DleAxisComments = Record<DleAxisKey, string[]>;

/** 1 期間 (月 or Q) の集計点。 @graph-connects none */
export interface DleScorePoint {
  /** "YYYY/MM" または "26Q3" */
  period: string;
  scores: DleAxisScores;
  counts: DleAxisCounts;
  /** この期間の回答件数 (アンケート通数) */
  answerCount: number;
  /** 4 軸の有効評点すべての平均 */
  overall: number | null;
  /** 軸別のコメント本文。全体平均の系列は常に空 */
  comments: DleAxisComments;
}

/** GET /dle-evaluation/my-scores・/scores のレスポンス。 @graph-connects none */
export interface MyDleScoresResponse {
  /** 対象の nickname（正規化済み） */
  dle: string;
  /** 対象が DLE か (現任名簿 or 評価実績あり)。本人のタブ表示判定に使う */
  isDle: boolean;
  monthly: DleScorePoint[];
  quarterly: DleScorePoint[];
  overallMonthly: DleScorePoint[];
  overallQuarterly: DleScorePoint[];
  overallDleCount: number;
  /** 他人の DLE 評価を読めるか。タブに対象切替を出すかの判定 */
  canViewOthers: boolean;
  /** 切替で選べる DLE。`canViewOthers` が false なら常に空 */
  dles: string[];
}

/** radar / 推移の軸 1 本。 @graph-connects none */
export interface DleScoreAxis {
  key: DleAxisKey;
  /** 推移折線とスイッチ pill の色 */
  color: string;
  /** i18n ラベルキー */
  labelKey: string;
}

/**
 * 評価 4 軸。**並び順がそのまま radar の頂点の並び**になる (4 軸なので菱形)。
 *
 * 色は紫系の 1 本のランプ (浅 → 深)。「個人評価は紫」という配色規則を推移モードでも
 * 保ちつつ、軸スイッチ pill をどれが選択中か見分けられるようにするため、
 * デザイン評価の PJ解像度 (青緑ランプ) と同じ作りにしている。
 *
 * ランプは purple-400 から始める。**最も淡い purple-300 は `DLE_RADAR_SELF_PREV_COLOR`
 * (前期間の個人評価) 専用に空けてある** — 推移モードに前期間系列は無いので実害は無いが、
 * 同じ淡紫が画面によって別の意味になるのを避ける。
 * @graph-connects none
 */
export const DLE_SCORE_AXES: readonly DleScoreAxis[] = [
  { key: 'firstResponse', color: '#c084fc', labelKey: 'dleEvaluation.axes.firstResponse' },
  { key: 'sparring', color: '#a855f7', labelKey: 'dleEvaluation.axes.sparring' },
  { key: 'responseSpeed', color: '#9333ea', labelKey: 'dleEvaluation.axes.responseSpeed' },
  { key: 'intent', color: '#6b21a8', labelKey: 'dleEvaluation.axes.intent' },
];

/** radar の個人評価系列色 (紫)。 @graph-connects none */
export const DLE_RADAR_SELF_COLOR = '#a855f7';
/**
 * radar の「前期間の個人評価」参照系列色 (淡紫)。
 * 当期間 (紫) との対比で前期間からの伸びを示す。
 * @graph-connects none
 */
export const DLE_RADAR_SELF_PREV_COLOR = '#d8b4fe';

/**
 * radar の半径スケール ([0, 満点])。評点は 1〜5 だが、デザイン評価と同じく 0 起点にして
 * 「満点に対してどこまで届いているか」を面積で読めるようにする。
 * @graph-connects none
 */
export const DLE_RADIUS_DOMAIN: [number, number] = [0, 5];

/**
 * 推移折線の y 軸スケール。
 *
 * 下限は評点の下限 1 に合わせる。デザイン評価の貢献度は実績が 2 を下回らないため y 軸を
 * [2,5] に詰めているが、DLE 評価は 1 点が選べる尺度で実際に 2.25 まで下がった月がある。
 * 下限を詰めると将来の低評価が裁切られて「下がった」ことが読めなくなる。
 * @graph-connects none
 */
export const DLE_TREND_DOMAIN: [number, number] = [1, 5];
